import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { apolloClient } from "@/service/apollo-provider";
import gql from "graphql-tag";
import {
  Account,
  Configuration,
  Quota,
  QuotaUsageType,
  PersistedExportSelection,
  ExportType
} from "@/motion-cloud";
import { Account as MsalAccount } from "msal";
import { settings } from "@xsenscloud/xse-styleguide";
import { Route } from "vue-router";

export const QUOTA_LIMIT_REACHED = 0.8;

export const blankAccount: Account = {
  id: -1,
  avatar: "",
  email: "test@trimm.nl",
  name: "Test",
  organization: "TRIMM",
  permissions: [],
  roles: {
    organization_admin: false,
    system_admin: false
  },
  sub: "0000-0000-00000",
  token: null
};

@Module({ namespaced: true, preserveState: true })
class UserModule extends VuexModule {
  account: Account = blankAccount;
  skipSaveAndAnalyseDialog = false;
  messages: Array<string> = [];
  configuration: Configuration[] = [];
  routeAfterAuthentication: null | Partial<Route> = null;
  quota: Quota[] = [];
  lastExportSettings: PersistedExportSelection = {
    options: {
      exporter: ExportType.BVH,
      customSettings: {}
    }
  };

  get getFullAccount() {
    return this.account;
  }

  get sub() {
    return this.account.sub;
  }

  get owner() {
    return this.account.id;
  }

  get applications() {
    return ((settings.APPLICATIONS as unknown) as Configuration[]).filter(
      config => {
        if (config.permissions.length === 0) {
          return true;
        } else {
          const hasPermissions = config.permissions.reduce(
            (granted: boolean, permission: string) => {
              return (
                this.account.permissions.indexOf(permission) !== -1 && granted
              );
            },
            true
          );

          let hasRole = false;

          if (
            (!config.roles.system_admin && !config.roles.organization_admin) ||
            (config.roles.system_admin && this.account.roles.system_admin) ||
            (config.roles.organization_admin &&
              this.account.roles.organization_admin)
          ) {
            hasRole = true;
          }
          return hasPermissions && hasRole;
        }
        return false;
      }
    );
  }

  get portalHome() {
    return ((settings.APPLICATIONS as unknown) as Configuration[]).find(
      config => config.type === "portal-home"
    );
  }

  get portalBilling() {
    return ((settings.APPLICATIONS as unknown) as Configuration[]).find(
      config => config.id === "billing"
    );
  }

  get noQuotaRemaining() {
    const quota = this.quota.find(
      q => q.quotum_group === QuotaUsageType.SECONDS
    );
    if (quota) {
      return quota.usage_quotum === -1
        ? false // Unlimited always has remaining quota
        : quota.usage_used >= quota.usage_quotum;
    }
    return true;
  }

  get quotaLimitReached() {
    const quota = this.quota.find(
      q => q.quotum_group === QuotaUsageType.SECONDS
    );
    if (quota && quota.usage_quotum !== -1) {
      return (
        1 - (quota.usage_quotum - quota.usage_used) / quota.usage_quotum >=
        QUOTA_LIMIT_REACHED
      );
    }
    return false;
  }

  @Mutation
  setQuota(quota: Quota[]) {
    this.quota = [...quota];
  }

  @Mutation
  setRouteAfterAuthentication(routeAfterAuthentication: Partial<Route> | null) {
    this.routeAfterAuthentication = routeAfterAuthentication;
  }
  @Mutation
  clearState() {
    this.account = {
      ...blankAccount,
      token: ""
    };
  }

  @Mutation
  setAccount(account: Account) {
    this.account = {
      ...this.account,
      ...account
    };
  }

  @Mutation
  setLastExportSettings(exportSettings: PersistedExportSelection) {
    this.lastExportSettings = exportSettings;
  }

  @Mutation
  setSkipSaveAndAnalyseDialog(skip: boolean) {
    this.skipSaveAndAnalyseDialog = skip;
  }

  @Mutation
  setMessages(messages: Array<string>) {
    this.messages = messages;
  }

  @Mutation
  setConfiguration(configuration: Configuration[]) {
    this.configuration = configuration;
  }

  /**
   * Gets the current account (owner) id
   * @returns {Promise<void>}
   */
  @Action
  async currentAccountId() {
    const { data } = await apolloClient.mutate({
      mutation: gql`
        mutation getCurrentAccountId {
          currentAccountId(input: {}) {
            integer
          }
        }
      `,
      fetchPolicy: "no-cache"
    });

    this.context.commit("setAccount", {
      id: data.currentAccountId.integer
    });
  }

  /**
   * delete token and reset state
   */
  @Action({ commit: "clearState" })
  logout() {
    //
  }

  /**
   * Get own account info
   * @param {MsalAccount} account
   */
  @Action
  async getAccount(account: MsalAccount) {
    const { data } = await apolloClient.query({
      query: gql`
        query getAccountBySub($sub: String!) {
          accountBySub(sub: $sub) {
            id
          }
        }
      `,
      fetchPolicy: "no-cache",
      variables: {
        sub: account.accountIdentifier
      }
    });

    this.context.commit("setAccount", {
      id: data.accountBySub.id
    });
  }
}

export default UserModule;
